/**
 * The state of the migration from rate cards and schedule rules into
 * Products and the strategies' own cadence (CU-869fhuhy6, UX D11/D23).
 *
 * `GET /api/repricing/migration` → `{ pending, validated_at, report }`,
 * read from `tenant_config`. Read-only: the human OK is written by the
 * engineer with the migration script, never by a button. While `pending`
 * the screens keep the old windows reachable read-only and show the
 * banner; once validated both go.
 *
 * `pending` is `null` until the server answers, so nothing is shown or
 * hidden on a guess. A failed read keeps the legacy windows (`pending:
 * true`) and says why — retiring them on an outage would be the one
 * mistake nobody could undo from the screen.
 */
import { useCallback, useEffect, useState } from 'react'
import { BACKEND_APIS } from '@constants/routes'
import { makeClient } from 'Pages/DsRepricing/@services/http'

const http = makeClient(BACKEND_APIS.GENERAL_REPRICING_API)

export function useMigration() {
	const [state, setState] = useState({ loading: true, pending: null, validatedAt: null, report: null, error: null })
	const load = useCallback(async () => {
		setState(s => ({ ...s, loading: true }))
		try {
			const body = await http.get('/migration')
			setState({ loading: false, pending: body?.pending !== false, validatedAt: body?.validated_at || null,
			           report: body?.report || null, error: null })
		} catch (e) {
			setState({ loading: false, pending: true, validatedAt: null, report: null,
			           error: e?.message || 'Could not read the migration state' })
		}
	}, [])
	useEffect(() => { load() }, [load])
	return { ...state, reload: load }
}

/** `true` while the migration awaits the human OK, `false` once validated, `null` until known. */
export function useMigrationPending() {
	return useMigration().pending
}
